import styled from 'styled-components';
import { Checkbox, Button, Col } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { Input as CommonInput } from '@app/components/common/inputs/Input/Input';
import { InputPassword as CommonInputPassword } from '@app/components/common/inputs/InputPassword/InputPassword';
import { BORDER_RADIUS, FONT_SIZE, FONT_WEIGHT, media } from '@app/styles/themes/constants';
import backgroundWrapper from '@app/assets/images/body.png';
import footerBackground from '@app/assets/images/footer.png';

/** Phones only: every rule below this query leaves 768px and wider untouched. */
const PHONE = '(max-width: 767.98px)';

export const Wrapper = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
`;

export const OverlayImage = styled.div`
  position: absolute;
  width: 100%;
  z-index: 3;
  object-fit: cover;
`;

export const BackgroundHeader = styled.div`
  display: flex;
  position: absolute;
  width: 100%;
  z-index: 2;
  background-color: white;
  top: 0;
  height: 5.5rem;
`;

export const TopLeft = styled.div`
  color: var(--primary-color);
  font-size: 3rem;
  font-weight: 700;
  margin-left: 1.2rem;
  margin-top: 1.2rem;
  width: 60%;

  /* Login header on phones: 3rem ran to 3 lines under the logo; shrink to fit 2 lines beside it. */
  @media ${PHONE} {
    flex: 1 1 0;
    min-width: 0;
    width: auto;
    align-self: center;
    margin: 0 0 0 0.75rem;
    font-size: clamp(1rem, 6vw, 2rem);
    line-height: 1.15;
  }
`;

export const TextHeaderLeft = styled.div`
  color: var(--primary-color);
  font-size: 3rem;
  font-weight: 700;
  margin-left: 1.2rem;

  /*
   * Picker header on phones: the controls on the right keep ~12rem, so the size follows the width left for the title
   * ("VNFF- FACTORY" is ~8.5em with its margin) and the title takes 2 lines at most.
   */
  @media ${PHONE} {
    min-width: 0;
    margin-left: 0.75rem;
    font-size: clamp(0.875rem, (100vw - 12rem) / 8.5, 2rem);
    line-height: 1.15;
  }
`;

/** Picker header, title column: on phones it takes whatever the controls leave and may shrink below its text. */
export const HeaderTitleCol = styled(Col)`
  @media ${PHONE} {
    && {
      flex: 1 1 0;
      max-width: none;
      min-width: 0;
    }
  }
`;

/** Picker header, controls column: on phones it is as wide as its controls, without wrapping them. */
export const HeaderControlsCol = styled(Col)`
  @media ${PHONE} {
    && {
      flex: 0 0 auto;
      max-width: none;
    }

    && .ant-row {
      flex-wrap: nowrap;
    }

    && .ant-btn-icon-only {
      width: 36px;
      height: 36px;
    }
  }
`;

/**
 * Picker header, fullscreen toggle: hidden on phones to leave room for the title. The Fullscreen API is not supported
 * on iPhone Safari, so the button did nothing there anyway.
 */
export const HeaderFullscreenCol = styled(Col)`
  @media ${PHONE} {
    display: none;
  }
`;

export const LogoHitachi = styled.img`
  height: 3.8rem;

  @media ${PHONE} {
    height: 2.2rem;
  }
`;
export const TopRight = styled.div`
  width: 40%;
  display: flex;
  justify-content: flex-end;
  flex-wrap: nowrap;
  padding: 1rem 1rem 0 0;

  @media ${PHONE} {
    flex: 0 0 auto;
    width: auto;
    align-items: center;
    padding: 0 1rem 0 0.5rem;
  }
`;

export const BottomImage = styled.div`
  position: absolute;
  width: 100%;
  z-index: 3;
  object-fit: cover;
  bottom: 0;
  height: 16%;
  background: white;
  display: flex;
  justify-content: center;
  padding-top: 5px;
  gap: 10px;

  /* On phones the 7 photos ran ~500px past the screen and scrolled the page sideways: the strip scrolls instead. */
  @media ${PHONE} {
    justify-content: flex-start;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    gap: 6px;
  }
`;

export const FooterImage = styled.img`
  flex: 1;
  max-width: 100%;
  height: auto;

  @media ${PHONE} {
    flex: 0 0 auto;
    max-width: none;
    height: 100%;
    width: auto;
  }
`;

export const BackgroundWrapper = styled.div`
  background-image: url(${backgroundWrapper});
  background-size: cover;
  background-position: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

export const LoginWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

export const FormWrapper = styled.div`
  padding: 2.5rem;
  width: 31.75rem;
  overflow: auto;
  background-color: rgba(var(--background-rgb-color), 0.93);
  border-radius: ${BORDER_RADIUS};

  @media only screen and ${media.xs} {
    padding: 2.5rem 1.25rem;
    width: 20.75rem;
    max-height: calc(100vh - 3rem);
  }

  @media only screen and ${media.md} {
    padding: 2.5rem;
    width: 31.75rem;
    max-height: calc(100vh - 3rem);
  }

  /* Below 360px no rule above applies and the 31.75rem card scrolled the login page sideways. */
  @media ${PHONE} {
    padding: 2.5rem 1.25rem;
    max-width: calc(100vw - 2rem);
  }
`;

export const FormTitle = styled.div`
  color: var(--primary-color);

  @media only screen and ${media.xs} {
    margin-bottom: 0.625rem;
    font-size: ${FONT_SIZE.lg};
    font-weight: ${FONT_WEIGHT.bold};
    line-height: 1.5625rem;
  }

  @media only screen and ${media.md} {
    margin-bottom: 0.875rem;
    font-size: ${FONT_SIZE.xxl};
    font-weight: ${FONT_WEIGHT.bold};
    line-height: 1.9375rem;
  }

  @media only screen and ${media.xl} {
    margin-bottom: 0.9375rem;
    font-size: ${FONT_SIZE.xxxl};
    font-weight: ${FONT_WEIGHT.extraBold};
    line-height: 2.125rem;
  }
`;

export const FormCheckbox = styled(Checkbox)`
  display: flex;
  padding-left: 0.125rem;

  & .ant-checkbox-inner {
    border-radius: 3px;
    transform: scale(1.375);
  }

  & .ant-checkbox-input {
    transform: scale(1.375);
  }
`;

export const FormItem = styled(BaseForm.Item)`
  margin-bottom: 0.75rem;
  & .ant-form-item-control-input {
    min-height: 3.125rem;
  }

  & .ant-form-item-explain-error {
    font-size: ${FONT_SIZE.xs};
  }

  & label {
    color: var(--primary-color);
    font-size: ${FONT_SIZE.xs};
    line-height: 1.25rem;
  }

  &.ant-form-item-has-feedback .ant-input-affix-wrapper .ant-input-suffix {
    padding-right: 1.5rem;
  }
`;

export const FormInput = styled(CommonInput)`
  color: var(--text-main-color);
  background: transparent;

  & input.ant-input {
    background: transparent;
  }
`;

export const FormInputPassword = styled(CommonInputPassword)`
  color: var(--text-main-color);
  background: transparent;

  & input.ant-input {
    background: transparent;
  }
`;

export const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

export const Text = styled.span`
  color: var(--text-main-color);
  font-size: ${FONT_SIZE.xs};
  font-weight: ${FONT_WEIGHT.regular};
`;

export const LinkText = styled(Text)`
  text-decoration: underline;
  color: var(--primary-color);
`;

export const SubmitButton = styled(Button)`
  font-size: ${FONT_SIZE.md};
  font-weight: ${FONT_WEIGHT.semibold};
  width: 100%;
`;

export const SocialButton = styled(Button)`
  font-size: ${FONT_SIZE.md};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  width: 100%;
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
`;

export const FooterWrapper = styled.div`
  margin-top: 1.25rem;
  text-align: center;
`;

export const BackIcon = styled(LeftOutlined)`
  font-size: 0.75rem;
  margin-right: 0.75rem;
`;

export const BackWrapper = styled.div`
  font-size: ${FONT_SIZE.md};
  font-weight: ${FONT_WEIGHT.semibold};
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 1.25rem;
`;

export const SocialIconWrapper = styled.div`
  display: flex;
  margin-right: 0.8125rem;
  @media only screen and ${media.xs} {
    margin-right: 0.625rem;
  }

  @media only screen and ${media.md} {
    margin-right: 0.8125rem;
  }
`;
